import { applicationStore } from '../../../store/applicationStore';
import {
  CommonConnectionsMethod,
  ConnectionPrefix,
  GameLiveConnectionEvent,
  GameLiveConnectionMethod,
} from '../../constants/connectionTypes';
import { BaseLiveConnection } from '../baseLiveConnection';

export class GameLiveConnection extends BaseLiveConnection {
  constructor({ url }) {
    super(url);
  }

  sendGetInitialState(getInitialParams) {
    return this.invoke(
      applicationStore.isDemo
        ? GameLiveConnectionMethod.GetDemoInitialState
        : GameLiveConnectionMethod.GetInitialState,
      getInitialParams
    )
      .then(() => {})
      .catch(() => {});
  }

  sendJackpotInitial(partnerId, gameId, currencySymbol) {
    return this.invoke(GameLiveConnectionMethod.JackpotConnect, partnerId, gameId, currencySymbol);
  }

  getBoostedJackpotWin(gameId) {
    return this.invoke(GameLiveConnectionMethod.GetBoostedJackpotWin, gameId);
  }

  sendJackpotWinnerInfo(getInitialParams) {
    return this.invoke(GameLiveConnectionMethod.JackpotWinnerInfo, getInitialParams);
  }

  getJackpotSymbol(model) {
    return this.invoke(GameLiveConnectionEvent.OpenJackpotSceneItem, model);
  }

  getJackpotScene() {
    return this.invoke(GameLiveConnectionEvent.OpenJackpotScene);
  }

  ping(...args) {
    if (this.connection) {
      return this.invoke(GameLiveConnectionMethod.Ping, ...args);
    }
    return null;
  }

  spin(...args) {
    if (this.connection) {
      return this.invoke(GameLiveConnectionMethod.Spin, ...args);
    }
    return null;
  }

  demoSpin(...args) {
    if (this.connection) {
      return this.invoke(GameLiveConnectionMethod.DemoSpin, ...args);
    }
    return null;
  }

  gamble(...args) {
    if (this.connection) {
      return this.invoke(GameLiveConnectionMethod.Gamble, ...args);
    }
    return null;
  }

  getTournamentInfo(...args) {
    if (this.connection) {
      return this.invoke(GameLiveConnectionMethod.GetTournamentInfo, ...args);
    }
    return null;
  }

  demoGamble(...args) {
    if (this.connection) {
      return this.invoke(GameLiveConnectionMethod.DemoGamble, ...args);
    }
    return null;
  }

  spinCollect(...args) {
    if (this.connection) {
      return this.invoke(GameLiveConnectionMethod.SpinCollect, ...args);
    }
    return null;
  }

  demoSpinCollect(...args) {
    if (this.connection) {
      return this.invoke(GameLiveConnectionMethod.DemoSpinCollect, ...args);
    }
    return null;
  }

  spinAvalanche(...args) {
    if (this.connection) {
      return this.invoke(GameLiveConnectionMethod.SpinAvalanche, ...args);
    }
    return null;
  }

  demoSpinAvalanche(...args) {
    if (this.connection) {
      return this.invoke(GameLiveConnectionMethod.DemoSpinAvalanche, ...args);
    }
    return null;
  }

  onGetInitialStateResult(callback) {
    return this.on(
      applicationStore.isDemo
        ? GameLiveConnectionEvent.DemoInitialStateResult
        : GameLiveConnectionEvent.InitialStateResult,
      (data) => {
        if (callback) {
          callback(data);
        }
        const { gameTypeId } = data;
        if (gameTypeId) {
          this.callbackMiddleware({
            methodName: `${ConnectionPrefix.Game}_${
              applicationStore.isDemo
                ? GameLiveConnectionEvent.DemoInitialStateResult
                : GameLiveConnectionEvent.InitialStateResult
            }`,
            data: gameTypeId,
          });
        }
      }
    );
  }

  onHandleUnauthorizedError(callback) {
    return this.on(GameLiveConnectionMethod.Error, (data) => {
      if (callback) {
        callback(data);
      }

      this.callbackMiddleware({
        methodName: GameLiveConnectionMethod.Error,
        data,
      });
    });
  }

  onBoostedJackpotActivationInfoResult(callback) {
    return this.on(GameLiveConnectionEvent.BoostedJackpotActivationInfoResult, callback);
  }

  onSpinAvalancheResult(callback) {
    return this.on(GameLiveConnectionEvent.SpinAvalancheResult, callback);
  }

  onDemoSpinAvalancheResult(callback) {
    return this.on(GameLiveConnectionEvent.DemoSpinAvalancheResult, callback);
  }

  onJackpotUpdate(callback) {
    return this.on(GameLiveConnectionEvent.JackpotUpdate, callback);
  }

  onJackpotWin(callback) {
    return this.on(GameLiveConnectionEvent.JackpotWin, callback);
  }

  onJackpotWinnerInfoResult(callback) {
    return this.on(GameLiveConnectionEvent.JackpotWinnerInfoResult, callback);
  }

  onJackpotSceneResult(callback) {
    return this.on(GameLiveConnectionEvent.JackpotSceneResult, callback);
  }

  onJackpotSceneItemResult(callback) {
    return this.on(GameLiveConnectionEvent.JackpotSceneItemResult, callback);
  }

  onBoostedJackpotWinnerInfoResult(callback) {
    return this.on(GameLiveConnectionEvent.BoostedJackpotWinnerInfoResult, callback);
  }

  onGetBoostedJackpotWinResult(callback) {
    return this.on(GameLiveConnectionEvent.GetBoostedJackpotWinResult, callback);
  }

  onBoostedJackpotExpirationInfoResult(callback) {
    return this.on(GameLiveConnectionEvent.BoostedJackpotExpirationInfoResult, callback);
  }


  onSpinResult(callback) {
    return this.on(GameLiveConnectionEvent.SpinResult, callback);
  }

  onFreeBetAwarded(callback) {
    return this.on(GameLiveConnectionEvent.FreeBetAwarded, callback);
  }

  onDemoSpinResult(callback) {
    return this.on(GameLiveConnectionEvent.DemoSpinResult, callback);
  }

  onMaintenanceNotify(callback) {
    return this.on(GameLiveConnectionMethod.MaintenanceNotify, (data) => {
      if (callback) {
        callback(data);
      }
      this.callbackMiddleware({
        methodName: CommonConnectionsMethod.UnderMaintenance,
        data,
      });
    });
  }

  onBalanceUpdate(callback) {
    return this.on(GameLiveConnectionMethod.GetBalanceResult, (data) => {
      if (callback) {
        callback(data);
      }

      this.callbackMiddleware({
        methodName: GameLiveConnectionMethod.GetBalanceResult,
        data,
      });
    });
  }

  onTournamentBalanceUpdate(callback) {
    return this.on(GameLiveConnectionMethod.GetTournamentBalanceResult, (data) => {
      if (callback) {
        callback(data);
      }

      this.callbackMiddleware({
        methodName: GameLiveConnectionMethod.GetTournamentBalanceResult,
        data,
      });
    });
  }

  onPing(callback) {
    return this.on(GameLiveConnectionMethod.Ping, callback);
  }

  onGameOpen(callback) {
    return this.on(GameLiveConnectionMethod.GameOpen, (data) => {
      if (callback) {
        callback(data);
      }

      this.callbackMiddleware({
        methodName: GameLiveConnectionMethod.GameOpen,
        data,
      });
    });
  }

  onGambleResult(callback) {
    return this.on(GameLiveConnectionEvent.GambleResult, callback);
  }

  onDemoGambleResult(callback) {
    return this.on(GameLiveConnectionEvent.DemoGambleResult, callback);
  }

  onSpinCollectResult(callback) {
    return this.on(GameLiveConnectionEvent.SpinCollectResult, callback);
  }

  onDemoSpinCollectResult(callback) {
    return this.on(GameLiveConnectionEvent.DemoSpinCollectResult, callback);
  }

  onTournamentInfoResult(callback) {
    return this.on(GameLiveConnectionEvent.GetTournamentInfoResult, callback);
  }
}
